import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Names } from '@mode-switch/model';
import {
  BasicChannel,
  EncField,
  EncSeries,
  Encoding,
  EncodingResult,
  MarkChannel,
  VariableType,
} from '@mode-vegas/vegas';
import { AccountFacade } from '@mode/accounts/contract';
import { ChartFacade, DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { ChartFormatHelpers, EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { CompiledAttr, CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { PillActionType } from 'libs/shared/contract-common/src/lib/charts';
import { Observable, Subject, of } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { DragEndData, DragStartData, FieldDropData, OnDropEvent } from '../../helpers/field-helpers';
import { EncodingUpdateType } from '../../marks-card/channels/channel.component';
import { ChannelSummary } from '../../marks-card/marks-helpers';
import { FieldDropHandlerService } from './field-drop-handler.service';

interface DropZoneContext {
  header: 'X-Axis' | 'Y-Axis';
  channel: 'column' | 'row' | 'value';
  fields: readonly EncField[];
  typeAheadDoesReplace: boolean;
  allowContinuous: boolean;
  allowDiscrete: boolean;
  allowScalars: boolean;
}

@Component({
  selector: 'mode-cartesian-builder-common',
  templateUrl: './cartesian-builder-common.component.html',
  styleUrls: ['./cartesian-builder-common.component.scss'],
})
export class CartesianBuilderCommonComponent implements OnDestroy, OnChanges {
  @Input({ required: true }) encoding!: Encoding;
  @Input() observables: any;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input() isExploring = false;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;

  queryToken$: Observable<string> | undefined;
  queryRunToken$: Observable<string> | undefined;
  colorPaletteToken$: Observable<string> | undefined;
  colorPaletteToken: string | undefined;
  view$: Observable<FlamingoTypes.ModeView> | undefined;
  vizToken$: Observable<string> | undefined;
  fieldFormatMap$: Observable<ChartTypes.FieldFormatMap> | undefined;
  fieldColorMap$: Observable<ChartTypes.FieldColorMap> | undefined;
  format$: Observable<ChartTypes.ChartFormat> | undefined;
  isAutoOrManualRender$: Observable<boolean> | undefined;
  renderModel$: Observable<RenderDataModel> | undefined;

  view: FlamingoTypes.ModeView | undefined;

  dragInfo: DragFieldInfo | undefined;

  secondaryFields: ReadonlyArray<EncField> = [];
  primaryFields: ReadonlyArray<EncField> = [];
  isHorizontalChart = false;
  xDropZoneContext: DropZoneContext | undefined;
  yDropZoneContext: DropZoneContext | undefined;

  series: EncSeries | undefined;
  colorMarkField: ChannelSummary | undefined;
  sizeMarkField: ChannelSummary | undefined;
  isValidForColor = false;
  canReplaceColorField = false;

  allColorPalettes: OrgTypes.ColorPalette[] = [];
  selectedColorPalette: OrgTypes.ColorPalette | undefined = undefined;
  defaultColorPalette: OrgTypes.ColorPalette | undefined = undefined;

  // The following depend on whether we are rendering with Muze vs HighCharts
  useMuzeRenderer = false;
  colorChannelZone: EncodingHelpers.ZoneType | undefined;
  sizeChannelZone: EncodingHelpers.ZoneType | undefined;

  private destroyed$ = new Subject<void>();

  constructor(
    private chartFacade: ChartFacade,
    private fieldDropHandler: FieldDropHandlerService,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
    private accountFacade: AccountFacade
  ) {
    this.chartFacade.dragField$.pipe(takeUntil(this.destroyed$)).subscribe((dragInfo: DragFieldInfo) => {
      this.dragInfo = dragInfo;
    });

    this.accountFacade.reportOwnerColorPalettes$.pipe(takeUntil(this.destroyed$)).subscribe((palettes) => {
      this.allColorPalettes = [...palettes]
        .filter((palette) => palette.type === OrgTypes.ColorPaletteType.Categorical)
        .sort((a, b) => a.name.localeCompare(b.name));
      this.defaultColorPalette = this.allColorPalettes.find((palette) => palette.isDefault);
    });

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('dragover', this.documentDragOver);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['observables']) {
      this.queryToken$ = this.observables.queryToken$;
      this.queryRunToken$ = this.observables.queryRunToken$;
      this.view$ = this.observables.view$;
      this.view$?.pipe(takeUntil(this.destroyed$)).subscribe((view) => (this.view = view));

      this.vizToken$ = this.isExploring ? of(FLAMINGO_RESERVED_REFS.Exploration) : this.observables.token$;
      this.fieldFormatMap$ = this.observables.fieldFormats$;
      this.fieldColorMap$ = this.observables.fieldColors$;
      this.format$ = this.observables.format$;
      this.format$?.pipe(takeUntil(this.destroyed$)).subscribe((format) => {
        this.useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(this.chartType, format);
        this.colorChannelZone = !this.useMuzeRenderer ? EncodingHelpers.ZoneType.Discrete : undefined;
        this.sizeChannelZone = !this.useMuzeRenderer ? EncodingHelpers.ZoneType.Aggregate : undefined;
      });

      this.colorPaletteToken$ = this.observables.colorPaletteToken$;
      this.colorPaletteToken$?.pipe(takeUntil(this.destroyed$)).subscribe((colorPaletteToken) => {
        this.colorPaletteToken = colorPaletteToken;
        this.selectedColorPalette = !this.colorPaletteToken
          ? this.defaultColorPalette
          : this.allColorPalettes.find((palette) => palette.token === this.colorPaletteToken);
      });

      this.isAutoOrManualRender$ = this.observables.isAutoOrManualRender$;
      this.renderModel$ = this.observables.renderModel$;
    }

    // Don't enable the color modal button until the chart is complete because we don't have the
    // data yet to show in the modal.
    this.isValidForColor = EncodingHelpers.isChartEncodingComplete(this.encoding, this.chartType);
    this.isHorizontalChart = EncodingHelpers.HorizontalChartTypes.has(this.chartType);
    this.primaryFields = this.fieldDropHandler.getPrimaryFields(this.encoding);
    this.secondaryFields = this.fieldDropHandler.getSecondaryFields(this.encoding, this.chartType);
    const primaryChannel = this.fieldDropHandler.getPrimaryChannel();

    this.series = this.encoding.marks.series[0];

    let colorField = this.encoding.marks.series[0]?.properties.color.values[0];
    if (colorField == null && this.chartType === 'linePlusBar') {
      // For the line+bar, Measure Names can't be added to the color channel until a
      // series is created (requires the user to add field to X and Y). This is different
      // from most other charts which are initialized with Measure Values as the series.
      // We still want Measure Names to appear in the color channel, so we create a
      // placeholder color field for configuring the channel. Measure Names can't be remove
      // or replaced in linePlusBar, so it doesn't need to be a real field anyway.
      colorField = {
        formula: Names.Key,
        variable: {
          type: VariableType.DISCRETE,
        },
      };
    }

    // Determine if the primary axis typeahead should use replace vs add.
    // Line+Bar will replace once there are two fields in y.
    // All others will replace if the color is a non-Measure Names field
    const typeAheadReplacesPrimary =
      this.chartType === 'linePlusBar' ? this.primaryFields.length > 1 : colorField?.formula !== Names.Key;

    const isGrouped = this.chartType === 'bar' || this.chartType === 'hBar';

    if (!this.isHorizontalChart) {
      this.xDropZoneContext = {
        header: 'X-Axis',
        channel: 'column',
        fields: this.secondaryFields,
        typeAheadDoesReplace: true,
        allowContinuous: !isGrouped,
        allowDiscrete: true,
        allowScalars: true,
      };

      this.yDropZoneContext = {
        header: 'Y-Axis',
        channel: primaryChannel,
        fields: this.primaryFields,
        typeAheadDoesReplace: typeAheadReplacesPrimary,
        allowContinuous: true,
        allowDiscrete: false,
        allowScalars: false,
      };
    } else {
      this.xDropZoneContext = {
        header: 'X-Axis',
        channel: primaryChannel,
        fields: this.primaryFields,
        typeAheadDoesReplace: typeAheadReplacesPrimary,
        allowContinuous: true,
        allowDiscrete: false,
        allowScalars: false,
      };

      this.yDropZoneContext = {
        header: 'Y-Axis',
        channel: 'row',
        fields: this.secondaryFields,
        typeAheadDoesReplace: true,
        allowContinuous: !isGrouped,
        allowDiscrete: true,
        allowScalars: true,
      };
    }

    this.canReplaceColorField =
      colorField != null && this.chartType !== 'linePlusBar' && this.primaryFields.length <= 1;
    const colorMark: ChannelSummary = {
      channel: 'color',
      type: colorField != null ? 'field' : 'empty',
      field: colorField,
      id: colorField?.id,
      fieldIds: colorField?.id != null ? [colorField.id] : [],
      isValid: true,
    };
    this.colorMarkField = colorMark;

    if (this.chartType === 'scatter') {
      const sizeField = this.encoding.marks.series[0]?.properties.size.values[0];
      const sizeMark: ChannelSummary = {
        channel: 'size',
        type: sizeField != null ? 'field' : 'empty',
        field: sizeField,
        id: sizeField?.id,
        fieldIds: sizeField?.id != null ? [sizeField.id] : [],
        isValid: true,
      };
      this.sizeMarkField = sizeMark;
    } else {
      this.sizeMarkField = undefined;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();

    this.document.removeEventListener('dragover', this.documentDragOver);
  }

  documentDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onFieldDrop({ field, channel }: FieldDropData) {
    if (this.dragInfo == null) {
      return;
    }
    const formula = this.dragInfo.formula;
    const source = this.dragInfo.source;

    if (formula == null) {
      return;
    }
    this.chartFacade.endFieldDrag();

    const newEncoding = this.fieldDropHandler.handleDropOnExistingField(
      this.encoding,
      field,
      formula,
      channel,
      source,
      this.useMuzeRenderer,
      this.getZoneType(formula, channel)
    );

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  onDrop($event: OnDropEvent) {
    const { event, channel, attr } = $event;
    const formula = attr ? attr : this.dragInfo?.formula;
    const source = this.dragInfo?.source;

    this.chartFacade.endFieldDrag();

    // Bail on empty formula
    if (formula == null) {
      return;
    }

    const zoneType = this.getZoneType(formula, channel);

    const newEncoding = this.fieldDropHandler.handleDropOnEmpty(
      this.view,
      this.encoding,
      this.chartType,
      formula,
      channel,
      source,
      event.index,
      this.useMuzeRenderer,
      zoneType
    );

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  setZoneTypeForNonMuzeCharts(channel: MarkChannel) {
    switch (channel) {
      case 'color':
        return EncodingHelpers.ZoneType.Discrete;
      case 'size':
        return EncodingHelpers.ZoneType.Aggregate;
      default:
        return undefined;
    }
  }

  onTypeAheadSelection($event: { attr: CompiledAttr; event: any; onFinalize: () => void }, channel: MarkChannel) {
    const { attr } = $event;
    const formula = attr;

    // Bail on empty formula
    if (formula == null) {
      return;
    }

    const newEncoding = EncodingHelpers.addOrReplaceVegasField(
      EncodingHelpers.loadEncoding(this.encoding),
      formula,
      channel,
      this.series ? this.series.field : this.encoding.marks.series.length ? undefined : '*',
      !this.useMuzeRenderer ? this.setZoneTypeForNonMuzeCharts(channel) : undefined
    );
    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  onDropOnTypeAhead($event: unknown, channel: MarkChannel) {
    const dragInfo: DragFieldInfo | undefined = this.dragInfo;
    const formula = dragInfo?.formula;
    const source = dragInfo?.source;

    this.chartService.endDrag();
    // Unclear what order of events would result in dragInfo.field being null, but it seems to be possible
    if (formula == null || source == null) {
      return;
    }

    // no-op
    if (source === channel) {
      return;
    }

    let newEncoding: EncodingResult | undefined;
    const series = this.series ? this.series : undefined;

    if (formula.isField && formula.alias) {
      const field = channel === 'color' ? this.colorMarkField?.field : this.sizeMarkField?.field;
      if (field?.id) {
        newEncoding = EncodingHelpers.replaceAndMoveVegasField(
          this.encoding,
          formula.alias,
          channel,
          field?.id ?? '',
          series ? series.field : this.encoding.marks.series.length ? undefined : '*',
          formula,
          !this.useMuzeRenderer ? this.setZoneTypeForNonMuzeCharts(channel) : undefined
        );
      }
    } else {
      newEncoding = EncodingHelpers.addOrReplaceVegasField(
        EncodingHelpers.loadEncoding(this.encoding),
        formula,
        channel,
        series ? series.field : this.encoding.marks.series.length ? undefined : '*',
        !this.useMuzeRenderer ? this.setZoneTypeForNonMuzeCharts(channel) : undefined
      );
    }

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  onDragStart({ field, source }: DragStartData) {
    if (this.view && [...this.secondaryFields, ...this.primaryFields].some((f) => f.id === field.id)) {
      const compiledField = CompiledField.fromEncField(this.view, field as EncField);
      this.chartFacade.startFieldDrag(compiledField, source);
    }
  }

  onDragEnd({ field }: DragEndData) {
    if (this.dragInfo == null) {
      return;
    }
    if (this.dragInfo.formula) {
      this.removeField({ $field: field });
      this.chartFacade.endFieldDrag();
    }
  }

  onFieldDragEnd() {
    this.chartFacade.endFieldDrag();
  }

  isDragging() {
    if (this.dragInfo?.formula?.isSystem) {
      return false;
    }

    return !!this.dragInfo?.formula || !!this.dragInfo?.groupId;
  }

  getZoneType(formula: CompiledFormula, channel: BasicChannel) {
    const primaryChannel = this.fieldDropHandler.getPrimaryChannel();
    if (channel === primaryChannel) {
      return EncodingHelpers.ZoneType.Aggregate;
    }
    const secondaryChannel = this.isHorizontalChart ? 'row' : 'column';
    if (channel === secondaryChannel) {
      const context = this.isHorizontalChart ? this.yDropZoneContext : this.xDropZoneContext;
      if (context?.allowContinuous === false) {
        return EncodingHelpers.ZoneType.Discrete;
      }
    }
    return formula.isFormulaNumeric || formula.isFormulaDate
      ? EncodingHelpers.ZoneType.Continuous
      : EncodingHelpers.ZoneType.Discrete;
  }

  fieldUpdated($event: { $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }) {
    const { $result, $formats } = $event;
    if ($formats != null) {
      this.observables.fieldFormats$.pipe(first()).subscribe((fieldFormats: ChartTypes.FieldFormatMap) => {
        // Merge the partial format update with the existing FieldFormatMap. The update in chart service
        // does a replace.
        this.chartService.dispatchEncodingAndFormatUpdate($result, undefined, { ...(fieldFormats ?? {}), ...$formats });
      });
    } else {
      this.chartService.dispatchEncodingUpdate($result, PillActionType.ChangePillFormula);
    }
  }

  removeField($event: { $field: EncField }) {
    const { $field } = $event;
    if ($field.id == null || $field.formula === Names.Key) {
      return;
    }
    let encResult = EncodingHelpers.removeFieldFromEncoding(this.encoding, this.chartType, $field.id);
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  removeSort($event: { $source: string | undefined }) {
    const { $source } = $event;
    if ($source != null) {
      this.chartService.removeSort($source);
    }
  }

  onPaletteAssigned(palette: OrgTypes.ColorPalette) {
    this.chartService.updateTheme('', palette.token, palette.value.split(','), true);
  }

  handleEncodingUpdate(update: {
    change: EncodingUpdateType;
    encodingResult: EncodingResult;
    fieldFormatMap: ChartTypes.FieldFormatMap;
  }) {
    let encodingResult = update.encodingResult;
    // There always needs to be a field on Color. If there is no color field, add Measure Names
    if (encodingResult.encoding.marks.series[0]?.properties.color.values.length === 0) {
      encodingResult = EncodingHelpers.addMeasureNamesToColorChannel(
        encodingResult.encoding,
        encodingResult.encoding.marks.series[0].field
      );
    }
    // Do the regular encoding update now
    this.chartService.dispatchEncodingAndFormatUpdate(encodingResult, undefined, update.fieldFormatMap);
  }
}
